import { Box } from '@modusign/design-system';
import cx from 'classnames';
import { MouseEventHandler, ReactNode } from 'react';

import TextBaseFieldPlaceholderValue from '@app/components/TextBaseField/TextBaseFieldPlaceholderValue';
import getFontFaceFontFamily from '@app/features/apply-react-signing/components/FieldRenderer/libs/field-font-family';
import TransformTextStyle from '@app/features/apply-react-signing/components/FieldSetupStep/Placeholders/TextBaseFieldPlaceholder/TransformTextStyle';
import createFieldClassNameByParticipantOrder from '@app/libs/participantFields/createFieldClassNameByParticipantOrder';
import PDFFontNormalizer from '@app/libs/reactPdfJs/utils/PDFFontNormalizer';

import './TextBaseFieldPlaceholder.scss';

export interface TextBaseFieldPlaceholderProps {
  fontPixelSize: number;
  participantOrder: number;
  rootElementSize: { height: number; width: number };
  star: boolean;
  style: Partial<TransformTextStyle>;
  value: ReactNode;
  onMouseDown: MouseEventHandler<HTMLDivElement>;
}

const defaultProps = {
  fontPixelSize: 16,
  participantOrder: 1,
  rootElementSize: null,
  star: false,
  style: null,
  onMouseDown(_event: MouseEventHandler<HTMLDivElement>) {},
};

let svgID = 0;

function TextBaseFieldPlaceholder({
  fontPixelSize,
  participantOrder,
  rootElementSize,
  star,
  style,
  value,
  onMouseDown,
}: TextBaseFieldPlaceholderProps) {
  const expandsStyle = { ...style, fontWeight: 400 };
  const adjustedStyle = PDFFontNormalizer.toPixelSizeStyleProps(expandsStyle, 1.0);
  if (expandsStyle.fontFamily) {
    adjustedStyle.fontFamily = getFontFaceFontFamily(expandsStyle.fontFamily);
  }
  svgID += 1;

  const TextBaseFieldPlaceholderClassByOrder = createFieldClassNameByParticipantOrder(
    'TextBaseFieldPlaceholder',
    participantOrder
  );

  return (
    <Box // eslint-disable-line jsx-a11y/no-static-element-interactions
      className={cx({ TextBaseFieldPlaceholder: true }, TextBaseFieldPlaceholderClassByOrder)}
      style={rootElementSize ?? undefined}
      onMouseDown={onMouseDown}
    >
      {star && <div className="TextBaseFieldPlaceholder__star">*</div>}
      <div className="TextBaseFieldPlaceholder__value">
        <TextBaseFieldPlaceholderValue transformTextStyle={adjustedStyle} value={value} />
      </div>
      <div className="TextBaseFieldPlaceholder__line-guide">
        <svg height="100%" width="100%">
          <defs>
            <pattern
              height={fontPixelSize}
              id={`TextBaseFieldPlaceholder__line-guide-svg-${svgID}`}
              patternUnits="userSpaceOnUse"
              viewBox="0 0 26 26"
              width={fontPixelSize}
            >
              <g fill="none" fillOpacity="0.15" fillRule="evenodd">
                <rect fill="black" height="1" width="9" x="2" y="24" />
                <rect fill="black" height="1" width="9" x="14.991" y="24" />
              </g>
            </pattern>
          </defs>
          <rect
            fill={`url(#TextBaseFieldPlaceholder__line-guide-svg-${svgID})`}
            height="100%"
            width="100%"
          />
        </svg>
      </div>
    </Box>
  );
}

TextBaseFieldPlaceholder.defaultProps = defaultProps;

export default TextBaseFieldPlaceholder;
